// Abo/Billing-Domäne (W0) — reine, I/O-freie Logik (Stil von permissions.ts / operator.ts).
//
// Zweck: das ZWEITE Zugriffs-Tor der App. Cevio hat heute EIN Tor — die pro-NUTZER-Rechte
// (permissions.ts: "darf dieser Mitarbeiter das?"). Das Abo braucht ein davor liegendes Tor:
// pro-WERKSTATT-Entitlements ("hat diese Werkstatt dieses Modul überhaupt gebucht?").
//   nutzbar(area) = moduleActiveForArea(entitlements, area)  &&  canReadArea(user, area)
// Beide Tore bleiben strikt getrennt: das Rechte-System wird NICHT angefasst.
//
// Abgrenzung zu operator.ts: dort lebt die GROBE Lizenz-/Preis-Sicht des Betreibers
// (WorkshopStatus active|suspended|canceled, PLAN_PRICE_CENTS, MRR). Hier lebt der FEINE,
// provider-getriebene Abo-Lebenszyklus (BillingStatus) + das Modul-Gating + die Seat-Rechnung.
// Die grobe workshops.status wird aus dem BillingStatus gespiegelt (siehe accessLevelFor).
//
// Variante 1 ("pro Nutzer, alles an"): alle verkaufbaren Module sind aktiv → das Gating liegt
// verdrahtet bereit, ist aber unsichtbar. Späterer Wechsel auf Baukasten/Stufen = nur Konfig.

import { canReadArea, canWriteArea, type Area } from './permissions'
import { userLimitForPlan, type UserLike } from './rbac'

// ===== Module =====

/** Verkaufbare Zusatz-Module (Bündel aus AREAS). "Kern"-Bereiche gehören zu KEINEM Modul (immer an). */
export const MODULES = ['lager', 'personal', 'auswertungen'] as const
export type Module = (typeof MODULES)[number]

const MODULE_SET: ReadonlySet<string> = new Set(MODULES)
export function isModule(value: unknown): value is Module {
  return typeof value === 'string' && MODULE_SET.has(value)
}

/**
 * Welches Modul deckt einen Bereich ab. null = Kern (annahme/werkstatt/kunden/rechnungen/leistungen/
 * team/werkstatt_einstellungen) → immer verfügbar, unabhängig vom Abo.
 * ENTSCHEIDUNG offen: exakter Modul-Schnitt (Produkt), technisch hier zentral änderbar.
 */
export const MODULE_OF_AREA: Record<Area, Module | null> = {
  annahme: null,
  werkstatt: null,
  kunden: null,
  rechnungen: null,
  leistungen: null,
  team: null,
  werkstatt_einstellungen: null,
  inventar: 'lager',
  einkauf: 'lager',
  arbeitszeit: 'personal',
  planung: 'personal',
}

/** Modul für einen Bereich (null = Kern). */
export function moduleOfArea(area: Area): Module | null {
  return MODULE_OF_AREA[area]
}

/** Die Plantafel-Belegung (kein AREAS-Eintrag, siehe permissions.ts) gehört zum Personal-Modul. */
export const BELEGUNG_MODULE: Module = 'personal'

// ===== Entitlements (pro Werkstatt) =====

/** Minimale Sicht auf eine Entitlement-Zeile (DTO/DB-Zeile passen ohne Mapping). active: bool ODER 0/1. */
export interface EntitlementLike {
  module: string
  active?: boolean | number | null
}

/** Menge der AKTIV freigeschalteten Module aus rohen Entitlement-Zeilen (unbekannte Module werden verworfen). */
export function activeModules(rows: readonly EntitlementLike[] | null | undefined): Set<Module> {
  const set = new Set<Module>()
  if (!Array.isArray(rows)) return set
  for (const r of rows) {
    if (r && !!r.active && isModule(r.module)) set.add(r.module)
  }
  return set
}

/** Hat die Werkstatt dieses Modul freigeschaltet? */
export function workshopHasModule(active: ReadonlySet<Module>, module: Module): boolean {
  return active.has(module)
}

/** Ist der Bereich per Abo verfügbar? Kern-Bereiche (Modul null) sind immer verfügbar. */
export function moduleActiveForArea(active: ReadonlySet<Module>, area: Area): boolean {
  const m = MODULE_OF_AREA[area]
  return m === null || active.has(m)
}

// ===== Das kombinierte Tor (Abo × Recht) =====

/** Darf der Nutzer den Bereich SEHEN? = Modul per Abo aktiv UND Lese-Recht. */
export function canUseArea(user: UserLike | null | undefined, active: ReadonlySet<Module>, area: Area): boolean {
  return moduleActiveForArea(active, area) && canReadArea(user, area)
}

/** Darf der Nutzer den Bereich BEARBEITEN? = Modul per Abo aktiv UND Schreib-Recht. */
export function canEditArea(user: UserLike | null | undefined, active: ReadonlySet<Module>, area: Area): boolean {
  return moduleActiveForArea(active, area) && canWriteArea(user, area)
}

// ===== Abo-Lebenszyklus (provider-getrieben) =====

/** Feiner Abo-Status (aus dem Provider-Webhook gepflegt). Grobe workshops.status wird daraus gespiegelt. */
export const BILLING_STATUSES = ['trialing', 'active', 'past_due', 'canceled'] as const
export type BillingStatus = (typeof BILLING_STATUSES)[number]

const BILLING_STATUS_SET: ReadonlySet<string> = new Set(BILLING_STATUSES)
export function isBillingStatus(v: unknown): v is BillingStatus {
  return typeof v === 'string' && BILLING_STATUS_SET.has(v)
}

/**
 * Zugriffs-Level, das ein Abo-Zustand gewährt:
 *   'full'         — volle Nutzung (Testphase, aktiv, past_due innerhalb der Kulanzfrist)
 *   'readonly'     — nur lesen (past_due NACH der Kulanzfrist) — Änderungen gesperrt, Abo-Seite bleibt offen
 *   'billing_only' — nur die Abo-/Abrechnungsseite + Export (gekündigt / unbekannter Status)
 * Die Abo-Seite MUSS in JEDEM Level für den Chef erreichbar bleiben (sonst kann niemand zahlen).
 */
export type AccessLevel = 'full' | 'readonly' | 'billing_only'

/** Kulanzfrist (Tage) ab Zahlungsausfall, bis von 'full' auf 'readonly' heruntergestuft wird. ENTSCHEIDUNG offen. */
export const DEFAULT_GRACE_DAYS = 10
const DAY_MS = 86_400_000

/** Minimale Sicht auf den Abo-Kopf für die Zustandsmaschine. Zeiten als epoch-ms (Aufrufer parst DB-UTC → ms). */
export interface SubscriptionLike {
  billingStatus: string
  /** Zeitpunkt (epoch-ms), seit dem das Abo 'past_due' ist. null/undefined = keine laufende Frist. */
  pastDueSinceMs?: number | null
}

/**
 * Zugriffs-Level aus dem Abo-Zustand. Rein arithmetisch (nowMs/pastDueSinceMs als epoch-ms) — bewusst KEIN
 * Date-Parsing hier (vermeidet den UTC-/Zeitzonen-Bug; das Backend rechnet DB-UTC → ms mit seinem Datums-Helfer).
 */
export function accessLevelFor(
  sub: SubscriptionLike | null | undefined,
  nowMs: number,
  graceDays: number = DEFAULT_GRACE_DAYS,
): AccessLevel {
  const status = sub?.billingStatus
  if (status === 'trialing' || status === 'active') return 'full'
  if (status === 'past_due') {
    const since = sub?.pastDueSinceMs
    if (since == null) return 'full' // Frist läuft noch nicht bzw. unbekannt → nicht sofort strafen
    return nowMs - since > graceDays * DAY_MS ? 'readonly' : 'full'
  }
  // 'canceled' oder unbekannt/defekt → fail-closed auf billing_only (Abo-Seite bleibt erreichbar).
  return 'billing_only'
}

/** Erlaubt das Level lesenden Zugriff auf die Fach-Bereiche? (billing_only sperrt alles außer Abo/Export.) */
export function accessAllowsRead(level: AccessLevel): boolean {
  return level === 'full' || level === 'readonly'
}

/** Erlaubt das Level schreibende Änderungen? Nur 'full'. */
export function accessAllowsWrite(level: AccessLevel): boolean {
  return level === 'full'
}

// ===== Seats (Variante 1: Preis pro aktivem Nutzer) =====

/** Minimale Nutzer-Sicht für die Seat-Zählung. active: bool ODER 0/1; Superadmin zählt NIE. */
export interface SeatUserLike {
  active?: boolean | number | null
  isSuperadmin?: boolean | number | null
}

/** Belegte Seats = aktive Nutzer der Werkstatt OHNE den Superadmin (Betreiber-Konto). */
export function seatsUsed(users: readonly SeatUserLike[] | null | undefined): number {
  if (!Array.isArray(users)) return 0
  return users.reduce((n, u) => n + (!!u?.active && !u?.isSuperadmin ? 1 : 0), 0)
}

/** Monats-Nettobetrag in Cent = Seats × Preis pro Seat (Geld immer in ganzen Cent). */
export function monthlyTotalCents(seats: number, unitPriceCents: number): number {
  const s = Number.isFinite(seats) && seats > 0 ? Math.floor(seats) : 0
  const p = Number.isFinite(unitPriceCents) && unitPriceCents > 0 ? Math.floor(unitPriceCents) : 0
  return s * p
}

// ===== Abo-Preis & Testphase (pay-per-user; Startwerte, zentral hier änderbar) =====
// HINWEIS: dies ist die pay-per-user-Preiswahrheit der FEINEN Abrechnung (subscriptions.unit_price_cents).
// Sie steht neben operator.ts/PLAN_PRICE_CENTS (grobe Betreiber-/MRR-Sicht mit flachen Tarifen) — die beiden
// sind noch NICHT versöhnt (Operator-MRR sollte künftig aus seats×unit_price kommen). Siehe Backlog.

/** Netto-Monatspreis je aktivem Seat in Cent (19,90 € brutto @ 19 % USt = 1672 netto). */
export const UNIT_PRICE_CENTS_NET = 1672

/** Umsatzsteuersatz in Basispunkten (19,00 % = 1900) — für die Brutto-Anzeige/den Einzug. */
export const DEFAULT_VAT_BASIS_POINTS = 1900

/** Länge der kostenlosen Testphase in Tagen. */
export const DEFAULT_TRIAL_DAYS = 14

/** Bruttobetrag (inkl. USt) aus einem Nettobetrag, kaufmännisch auf ganze Cent gerundet. */
export function grossCents(netCents: number, vatBasisPoints: number = DEFAULT_VAT_BASIS_POINTS): number {
  const n = Number.isFinite(netCents) && netCents > 0 ? Math.floor(netCents) : 0
  return Math.round((n * (10000 + vatBasisPoints)) / 10000)
}

/** Einzuziehender BRUTTO-Monatsbetrag = USt auf den Netto-Monatsbetrag (seats × Netto-Seat-Preis). */
export function monthlyChargeCents(seats: number, unitPriceCentsNet: number, vatBasisPoints: number = DEFAULT_VAT_BASIS_POINTS): number {
  return grossCents(monthlyTotalCents(seats, unitPriceCentsNet), vatBasisPoints)
}

// ===== Seat-Proration (untermonatliche Seat-Zugänge) =====
// Wird ein Seat MITTEN in der laufenden Periode aktiviert, läuft er nicht bis zur nächsten Grenze gratis mit:
// wir berechnen den anteiligen NETTO-Betrag für die Resttage der Periode und buchen ihn auf die NÄCHSTE Rechnung
// (pending_addon). Rundung tagegenau, ganze Cent. Reine Arithmetik (Tage rechnet der Aufrufer aus DB-UTC → Tage).
// Reduzierungen (Deaktivieren/Kündigen) erzeugen NIE eine Gutschrift → kein Refund, wirkt erst zur nächsten Grenze.

/**
 * Anteiliger NETTO-Betrag EINES Seats für die Resttage der laufenden Periode (kaufmännisch auf ganze Cent gerundet).
 * `remainingDays`/`totalDays` sind ganze Tage; wird auf [0, totalDays] geklemmt. totalDays ≤ 0 → 0 (defensiv).
 */
export function prorateAddonNet(unitPriceCentsNet: number, remainingDays: number, totalDays: number): number {
  const price = Number.isFinite(unitPriceCentsNet) && unitPriceCentsNet > 0 ? Math.floor(unitPriceCentsNet) : 0
  const total = Number.isFinite(totalDays) && totalDays > 0 ? Math.floor(totalDays) : 0
  if (price === 0 || total === 0) return 0
  const rem = Number.isFinite(remainingDays) ? Math.min(total, Math.max(0, Math.floor(remainingDays))) : 0
  return Math.round((price * rem) / total)
}

/**
 * Einzuziehender BRUTTO-Betrag inkl. eines untermonatlichen Netto-Zuschlags (pending_addon): USt wird auf die SUMME
 * (Basis-Monatsnetto + Zuschlag-Netto) gerechnet — bit-identisch zu aboInvoiceTotals(...).grossTotal (der Beleg).
 */
export function monthlyChargeCentsWithAddon(
  seats: number,
  unitPriceCentsNet: number,
  addonCentsNet: number = 0,
  vatBasisPoints: number = DEFAULT_VAT_BASIS_POINTS,
): number {
  const addon = Number.isFinite(addonCentsNet) && addonCentsNet > 0 ? Math.floor(addonCentsNet) : 0
  return grossCents(monthlyTotalCents(seats, unitPriceCentsNet) + addon, vatBasisPoints)
}

// ===== Abo-Rechnung (Cevio → Werkstatt) — Rechnungssteller-Stammdaten + Beträge (reine Domäne) =====
// Cevio ist hier der LEISTENDE (Rechnungssteller), die Werkstatt der Leistungsempfänger. Eigene, von den
// §14-KUNDENrechnungen der Werkstatt strikt getrennte Nummernserie (global fortlaufend, siehe formatAboInvoiceNo).

/** Rechnungssteller-Stammdaten (Cevio) für den Rechnungs-Snapshot. USt-Satz siehe DEFAULT_VAT_BASIS_POINTS. */
export interface AboSeller {
  name: string
  legalName: string | null
  address: string
  zip: string
  city: string
  country: string
  /** §14-Pflichtangabe: Steuernummer ODER USt-IdNr (mindestens eines von beiden muss gesetzt sein). */
  taxNumber: string | null
  vatId: string | null
  email: string | null
  /** SEPA-Gläubiger-ID (Creditor Identifier) für die Lastschrift-Pflichtangaben/Vorabankündigung. */
  sepaCreditorId: string | null
}

/**
 * Rechnungssteller-Stammdaten von Cevio (Snapshot-Quelle für Abo-Rechnungen).
 * ⚠️ MUSTERDATEN — vor dem echten Go-Live durch die ECHTEN Firmen-/Steuerdaten ersetzen: `taxNumber` bzw.
 * `vatId` (mindestens eines ist §14-Pflichtangabe) und `sepaCreditorId` (echte GoCardless-Gläubiger-ID),
 * sonst sind die ausgestellten Rechnungen nicht rechtsgültig / nicht vorsteuerabzugsfähig.
 */
export const CEVIO_SELLER: AboSeller = {
  name: 'Cevio GmbH',
  legalName: 'Cevio GmbH',
  address: 'Musterstraße 1',
  zip: '12345',
  city: 'Musterstadt',
  country: 'Deutschland',
  taxNumber: null,
  vatId: null,
  email: 'rechnung@cevio-software.com',
  sepaCreditorId: null,
}

/** Abo-Rechnungsnummer: eigene Cevio-Serie `CEV-YYYY-NNNNNN` (global fortlaufend je Jahr, 6-stellig aufgefüllt). */
export function formatAboInvoiceNo(year: number, seq: number): string {
  return `CEV-${year}-${String(seq).padStart(6, '0')}`
}

/**
 * Netto/USt/Brutto einer Abo-Rechnung. Der Bruttobetrag ist BIT-IDENTISCH zu monthlyChargeCentsWithAddon (= dem
 * per SEPA eingezogenen Betrag): Netto = seats × Netto-Seat-Preis + `addonCentsNet` (untermonatliche Seat-Zugänge),
 * USt auf die Summe gerundet (Summenrundung), taxTotal = brutto − netto (so gilt immer netto + USt = brutto).
 */
export function aboInvoiceTotals(
  seats: number,
  unitPriceCentsNet: number,
  addonCentsNet: number = 0,
  vatBasisPoints: number = DEFAULT_VAT_BASIS_POINTS,
): { netTotal: number; taxTotal: number; grossTotal: number } {
  const addon = Number.isFinite(addonCentsNet) && addonCentsNet > 0 ? Math.floor(addonCentsNet) : 0
  const netTotal = monthlyTotalCents(seats, unitPriceCentsNet) + addon
  const grossTotal = grossCents(netTotal, vatBasisPoints)
  return { netTotal, taxTotal: grossTotal - netTotal, grossTotal }
}

// ===== Abrechnungszyklus (W3d) — reine Entscheidung, WELCHE Aktion für ein Abo jetzt ansteht =====

/** Minimale Sicht auf den Abo-Kopf für die Zyklus-Entscheidung (Zeiten als epoch-ms; Backend parst DB-UTC→ms). */
export interface CycleView {
  billingStatus: string
  /** Mandat vorhanden = einzugsfähig. */
  hasMandate: boolean
  trialEndMs: number | null
  currentPeriodEndMs: number | null
  /** Zeitpunkt (epoch-ms) einer VORGEMERKTEN Kündigung (Kündigung zum Periodenende). null/undefined = keine. */
  cancelAtMs?: number | null
  /**
   * true = GRATIS-Account (schuldet nichts, z. B. subscriptions.unit_price_cents ≤ 0). Dann wird an der Grenze
   * einzugsfrei verlängert — OHNE Mandat, OHNE Einzug, OHNE past_due. Der manuelle „kostenlos"-Schalter (Betreiber
   * setzt unit_price_cents=0 in D1) soll einen Account nie aussperren, obwohl er nichts schuldet.
   */
  free?: boolean
}

/**
 * Was ist für dieses Abo JETZT zu tun?
 *   'charge' — fällig UND (Mandat vorhanden ODER gratis) → verarbeiten + Periode weiterdrehen. Bei einem Gratis-
 *              Account (oder 0-Betrag) unterbleibt der eigentliche Einzug/Beleg im Cron (amount>0-Guard), die
 *              Periode dreht aber weiter und der Account bleibt aktiv (kein Mandat nötig).
 *   'cancel' — fällig, aber eine Kündigung ist zum Periodenende vorgemerkt (cancelAt erreicht) → beenden, NICHT einziehen
 *   'lapse'  — fällig, KEIN Mandat UND nicht gratis → auf past_due setzen (Kunde zur Zahlweg-Einrichtung anstoßen)
 *   'none'   — nichts zu tun (noch nicht fällig oder bereits gekündigt)
 * Die vorgemerkte Kündigung hat an der Grenze VORRANG vor dem Einzug: so behält der Kunde bis zum Periodenende
 * vollen Zugang (accessLevelFor bleibt 'full', solange der Status active/trialing ist), wird dann aber ohne einen
 * weiteren Einzug beendet (kein Refund, kein „Gratis-Monat").
 */
export type CycleAction = 'charge' | 'cancel' | 'lapse' | 'none'
export function billingCycleAction(v: CycleView, nowMs: number): CycleAction {
  const cancelDue = v.cancelAtMs != null && v.cancelAtMs <= nowMs
  // Gratis-Accounts brauchen kein Mandat → an der Grenze wie 'charge' behandeln (der Cron zieht bei 0 nichts ein).
  const dueAction: CycleAction = cancelDue ? 'cancel' : v.free || v.hasMandate ? 'charge' : 'lapse'
  if (v.billingStatus === 'trialing') {
    if (v.trialEndMs != null && v.trialEndMs <= nowMs) return dueAction
    return 'none'
  }
  if (v.billingStatus === 'active' || v.billingStatus === 'past_due') {
    if (v.currentPeriodEndMs != null && v.currentPeriodEndMs <= nowMs) return dueAction
    return 'none'
  }
  return 'none' // 'canceled' oder unbekannt
}

// ===== Dunning (W5) — Nachfassen bei Zahlungsausfall (reine Entscheidung, WAS heute an einem past_due-Abo ansteht) =====
// Der Cron läuft täglich → `daysPastDue` (ganze Tage seit pastDueSince) wächst pro Lauf um 1. Die Benachrichtigungs-
// STUFEN sind schwellwert-basiert (firen ab dem Schwelltag) und werden vom Aufrufer per stabilem dedupKey je Stufe
// GENAU EINMAL zugestellt; die erneuten Einzugsversuche sind exakt-tag-basiert (Idempotency-Key je Versuch schützt
// zusätzlich). So bekommt der Chef eine klare, nicht-spammende Eskalation: Erinnerung → „endet bald" → „Nur-Lese".

/** Nachfass-Stufe (steuert Text + Zustellzeitpunkt). */
export type DunningStage = 'reminder' | 'grace_ending' | 'readonly_reached'

/** Tag (seit Ausfall) des ersten erneuten Einzugsversuchs; der zweite liegt auf `graceDays - 3` (Warntag). */
export const DUNNING_FIRST_RETRY_DAY = 3

export interface DunningAction {
  /** Zuzustellende Stufe (null = an diesem Tag keine neue Meldung). Aufrufer dedupt je Stufe. */
  notify: DunningStage | null
  /** Einzug erneut versuchen (nur mit Mandat sinnvoll — der Aufrufer prüft das Mandat zusätzlich). */
  retryCharge: boolean
}

/**
 * Was ist heute für ein past_due-Abo fällig? `daysPastDue` = ganze Tage seit dem Frist-Anker (pastDueSince).
 * Zeitplan (Default-Kulanz 10 Tage): Tag 0+ Erinnerung · Tag 3 Retry · Tag 7 (grace-3) „endet bald" + Retry ·
 * ab Tag 10 „Nur-Lese erreicht". Rein arithmetisch, I/O-frei (Projekt-Konvention).
 */
export function dunningActionFor(daysPastDue: number, hasMandate: boolean, graceDays: number = DEFAULT_GRACE_DAYS): DunningAction {
  // Kulanzfrist abgelaufen: einmaliger „ist jetzt Nur-Lese"-Hinweis, keine weiteren Einzugsversuche.
  if (daysPastDue >= graceDays) return { notify: 'readonly_reached', retryCharge: false }
  const warningDay = graceDays - 3
  let notify: DunningStage | null = null
  if (daysPastDue >= warningDay) notify = 'grace_ending'
  else if (daysPastDue >= 0) notify = 'reminder'
  const retryCharge = hasMandate && (daysPastDue === DUNNING_FIRST_RETRY_DAY || daysPastDue === warningDay)
  return { notify, retryCharge }
}

// ===== Tarif-Modell (V2): Preis pro WERKSTATT statt pro Nutzer =====
// Ablösung des pay-per-seat-Modells (Entscheidung Leon 2026-07-28): der Preis hängt am gewählten TARIF der
// Werkstatt (Solo/Team/Werkstatt), je Tarif monatlich ODER jährlich. Die Nutzerzahl treibt NICHT mehr den
// Preis, sondern nur noch das Nutzerlimit (PLAN_USER_LIMIT, rbac.ts). Reine Arithmetik/Konstanten (kein I/O).

/** Die drei buchbaren Tarife (aufsteigend nach Leistung/Preis). Keys = workshops.plan / subscriptions.plan_tier. */
export const PLAN_TIERS = ['solo', 'team', 'werkstatt'] as const
export type PlanTier = (typeof PLAN_TIERS)[number]
const PLAN_TIER_SET: ReadonlySet<string> = new Set(PLAN_TIERS)
export function isPlanTier(v: unknown): v is PlanTier {
  return typeof v === 'string' && PLAN_TIER_SET.has(v)
}

/** Abrechnungsintervall eines Abos. */
export const BILLING_INTERVALS = ['monthly', 'yearly'] as const
export type BillingInterval = (typeof BILLING_INTERVALS)[number]
const BILLING_INTERVAL_SET: ReadonlySet<string> = new Set(BILLING_INTERVALS)
export function isBillingInterval(v: unknown): v is BillingInterval {
  return typeof v === 'string' && BILLING_INTERVAL_SET.has(v)
}

/** Rang der Tarife (höher = mehr Leistung/teurer) — Basis für Upgrade/Downgrade-Vergleich. */
export const PLAN_TIER_RANK: Record<PlanTier, number> = { solo: 0, team: 1, werkstatt: 2 }

/** Bei Jahreszahlung gutgeschriebene Monate (2 = „2 Monate gratis" → Jahrespreis = 10 × Monatspreis). */
export const ANNUAL_FREE_MONTHS = 2

/**
 * Netto-Monatspreis je Tarif in Cent — die EINE Preiswahrheit des Tarif-Modells. Der Jahrespreis wird daraus
 * ABGELEITET (× (12 − ANNUAL_FREE_MONTHS)), damit Monat/Jahr nie auseinanderdriften. Netto (B2B, zzgl. 19 % USt),
 * an easyWerkstatt (25/49/99) angelehnt — Entscheidung Leon 2026-07-28.
 */
export const PLAN_MONTHLY_CENTS_NET: Record<PlanTier, number> = {
  solo: 2900,
  team: 4900,
  werkstatt: 9900,
}

/** Netto-Preis eines Tarifs für ein Abrechnungsintervall (Jahr = Monat × (12 − ANNUAL_FREE_MONTHS)). */
export function planPriceCentsNet(tier: PlanTier, interval: BillingInterval): number {
  const monthly = PLAN_MONTHLY_CENTS_NET[tier]
  return interval === 'yearly' ? monthly * (12 - ANNUAL_FREE_MONTHS) : monthly
}

/** Maximale aktive Nutzer (ohne Superadmin) eines Tarifs — Alias auf PLAN_USER_LIMIT (rbac.ts, eine Wahrheit). */
export function planTierUserLimit(tier: PlanTier): number {
  return userLimitForPlan(tier)
}

/**
 * Einzuziehender BRUTTO-Betrag eines Tarifs inkl. eines aufgelaufenen Netto-Zuschlags (Upgrade-Proration).
 * USt wird auf die SUMME (Tarif-Netto + Zuschlag-Netto) gerechnet — bit-identisch zu tariffInvoiceTotals(...).grossTotal.
 */
export function tariffChargeCents(priceCentsNet: number, addonCentsNet: number = 0, vatBasisPoints: number = DEFAULT_VAT_BASIS_POINTS): number {
  const base = Number.isFinite(priceCentsNet) && priceCentsNet > 0 ? Math.floor(priceCentsNet) : 0
  const addon = Number.isFinite(addonCentsNet) && addonCentsNet > 0 ? Math.floor(addonCentsNet) : 0
  return grossCents(base + addon, vatBasisPoints)
}

/**
 * Netto/USt/Brutto einer Tarif-Abo-Rechnung: Netto = Tarif-Netto + Zuschlag (untermonatliche Upgrade-Proration),
 * USt auf die Summe (Summenrundung), taxTotal = brutto − netto (so gilt immer netto + USt = brutto). Der Brutto-
 * betrag ist BIT-IDENTISCH zu tariffChargeCents (= dem per SEPA eingezogenen Betrag).
 */
export function tariffInvoiceTotals(priceCentsNet: number, addonCentsNet: number = 0, vatBasisPoints: number = DEFAULT_VAT_BASIS_POINTS): { netTotal: number; taxTotal: number; grossTotal: number } {
  const base = Number.isFinite(priceCentsNet) && priceCentsNet > 0 ? Math.floor(priceCentsNet) : 0
  const addon = Number.isFinite(addonCentsNet) && addonCentsNet > 0 ? Math.floor(addonCentsNet) : 0
  const netTotal = base + addon
  const grossTotal = grossCents(netTotal, vatBasisPoints)
  return { netTotal, taxTotal: grossTotal - netTotal, grossTotal }
}

/**
 * Art eines Tarifwechsels (steuert, WANN er wirkt):
 *   'upgrade_now'         — höherer Tarif → SOFORT aktiv, Differenz anteilig (Upgrade-Proration auf nächste Rechnung)
 *   'downgrade_scheduled' — niedrigerer Tarif → erst zum nächsten Abrechnungszyklus (Nutzerlimit-Guard, s. canDowngradeTo)
 *   'interval_scheduled'  — gleicher Tarif, anderes Intervall → erst zum nächsten Zyklus (keine anteilige Rückrechnung)
 *   'noop'                — identisch (Tarif UND Intervall unverändert)
 */
export type PlanChangeKind = 'upgrade_now' | 'downgrade_scheduled' | 'interval_scheduled' | 'noop'
export function classifyPlanChange(
  current: { tier: PlanTier; interval: BillingInterval },
  next: { tier: PlanTier; interval: BillingInterval },
): PlanChangeKind {
  if (next.tier === current.tier && next.interval === current.interval) return 'noop'
  const cr = PLAN_TIER_RANK[current.tier]
  const nr = PLAN_TIER_RANK[next.tier]
  if (nr > cr) return 'upgrade_now'
  if (nr < cr) return 'downgrade_scheduled'
  return 'interval_scheduled' // gleicher Tarif, nur Intervall gewechselt
}

/**
 * Anteiliger NETTO-Zuschlag beim Tarif-UPGRADE mitten in der Periode: die Preis-DIFFERENZ (neuer − alter Netto-
 * Preis, beide beim AKTUELLEN Intervall) für die Resttage der laufenden Periode (kaufmännisch, ganze Cent).
 * Nutzt dieselbe Tage-Arithmetik wie prorateAddonNet. Nicht-Upgrade (Differenz ≤ 0) → 0 (defensiv).
 */
export function prorateTierUpgradeNet(
  currentPriceCentsNet: number,
  nextPriceCentsNet: number,
  remainingDays: number,
  totalDays: number,
): number {
  const cur = Number.isFinite(currentPriceCentsNet) ? Math.floor(currentPriceCentsNet) : 0
  const nxt = Number.isFinite(nextPriceCentsNet) ? Math.floor(nextPriceCentsNet) : 0
  const diff = nxt - cur
  if (diff <= 0) return 0
  return prorateAddonNet(diff, remainingDays, totalDays)
}

/** Downgrade auf `tier` nur zulässig, wenn die aktiven Seats das Nutzerlimit des Zieltarifs einhalten. */
export function canDowngradeTo(tier: PlanTier, activeSeats: number): boolean {
  const seats = Number.isFinite(activeSeats) && activeSeats > 0 ? Math.floor(activeSeats) : 0
  return seats <= planTierUserLimit(tier)
}

/**
 * Perioden-Ende ab einem Start (epoch-ms) für das Intervall: +1 Monat (monatlich) / +1 Jahr (jährlich).
 * UTC-basiert; JS normalisiert Überläufe (z. B. 31.01. → 03.03., 29.02. → 28.02.). Backend rechnet DB-UTC → ms.
 */
export function nextPeriodEndMs(startMs: number, interval: BillingInterval): number {
  const d = new Date(startMs)
  if (interval === 'yearly') d.setUTCFullYear(d.getUTCFullYear() + 1)
  else d.setUTCMonth(d.getUTCMonth() + 1)
  return d.getTime()
}
